import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Users, Plus, Loader2, Edit2, Trash2, Shield, X, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function UserManagementPage() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'agent', department: '', agent_number: '', agent_name: '', supervised_by: '', is_active: true });

  const fetchUsers = async () => {
    try {
      const res = await api.getUsers();
      const userList = res || [];
      setUsers(userList);
      setSupervisors(userList.filter((u) => u.role === 'supervisor'));
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        const updateData = { ...form };
        delete updateData.email;
        delete updateData.password;
        if (!updateData.password) delete updateData.password;
        if (!updateData.supervised_by) delete updateData.supervised_by;
        if (!updateData.agent_number) delete updateData.agent_number;

        await api.updateUser(editUser.id, updateData);
        toast.success('User updated');
      } else {
        const createData = { ...form };
        if (!createData.supervised_by) delete createData.supervised_by;
        if (!createData.agent_number) delete createData.agent_number;

        await api.createUser(createData);
        toast.success('User created');
      }

      setShowModal(false);
      setEditUser(null);
      setForm({ email: '', password: '', name: '', role: 'agent', department: '', agent_number: '', agent_name: '', supervised_by: '', is_active: true });
      fetchUsers();
    } catch (err) {
      toast.error(editUser ? 'Failed to update user' : 'Failed to create user');
    }
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setForm({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      department: user.department || '',
      agent_number: user.agent_number || '',
      agent_name: user.agent_name || '',
      supervised_by: user.supervised_by || '',
      is_active: user.is_active !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.deleteUser(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleSyncAgents = async () => {
    setSyncing(true);
    try {
      const res = await api.syncAgents();
      toast.success(res.message);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to sync agents');
    } finally {
      setSyncing(false);
    }
  };

  if (!isAdmin) {
    return <div className="text-center py-12 text-muted-foreground">Admin access required</div>;
  }

  const roleBadge = (role) => role === 'admin' ? 'role-admin' : role === 'supervisor' ? 'role-supervisor' : 'role-agent';
  const agentCount = users.filter(u => u.role === 'agent').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{users.length} users ({agentCount} agents)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={handleSyncAgents} disabled={syncing} data-testid="users-sync-agents-button">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sync Agents
          </Button>
              <Button size="sm" onClick={() => { setEditUser(null); setForm({ email: '', password: '', name: '', role: 'agent', department: '', agent_number: '', agent_name: '', supervised_by: '', is_active: true }); setShowModal(true); }} data-testid="users-create-button">
            <Plus className="w-4 h-4 mr-1" /> Add User
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-card border border-border rounded-xl px-4 py-3 text-xs text-muted-foreground">
        Agents are auto-synced from SmartFlo call records. Default login: <span className="text-foreground font-medium">Agent Name</span> / <span className="text-foreground font-medium">Agent Name</span> (name as both username &amp; password).
        Click <strong>Sync Agents</strong> to manually refresh the list.
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="users-table">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                 <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Name</th>
                 <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Email</th>
                 <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Role</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Supervisor</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Department</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Agent Name</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Agent Number</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase px-4 py-3">Actions</th>
                </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className="border-b border-border/50 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-sm font-medium">{user.name}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{user.email}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${roleBadge(user.role)}`}>{user.role}</span></td> 
                  <td className="px-4 py-3 text-sm text-muted-foreground">{supervisors.find((s) => s.id === user.supervised_by)?.name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{user.department || '-'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{user.agent_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{user.agent_number || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${user.is_active !== false ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>{user.is_active !== false ? 'Active' : 'Disabled'}
                    </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(user)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => handleDelete(user.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                      </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{editUser ? 'Edit User' : 'Create User'}</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="user-name" className="text-xs text-muted-foreground mb-1 block">Name</label>
                <Input id="user-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              {!editUser && (
                <>
                  <div>
                    <label htmlFor="user-email" className="text-xs text-muted-foreground mb-1 block">Email</label>
                    <Input id="user-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                  </div>
                  <div>
                    <label htmlFor="user-password" className="text-xs text-muted-foreground mb-1 block">Password</label>
                    <Input id="user-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required />
                  </div>
                </>
              )}
              <div>
                <label htmlFor="user-role" className="text-xs text-muted-foreground mb-1 block">Role</label>
                <select
                  id="user-role"
                  value={form.role}
                  onChange={(e) => setForm({...form, role: e.target.value})}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                  data-testid="users-role-select"
                >
                  <option value="agent">Agent</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label htmlFor="user-department" className="text-xs text-muted-foreground mb-1 block">Department</label>
                <Input id="user-department" value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Agent Number (SmartFlo)</label>
                <Input value={form.agent_number} onChange={(e) => setForm({...form, agent_number: e.target.value})} placeholder="e.g. 919949161051" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Agent Name (SmartFlo)</label>
                <Input value={form.agent_name} onChange={(e) => setForm({...form, agent_name: e.target.value})} placeholder="Must match SmartFlo agent name" />
              </div>
              <div>
                <label htmlFor="user-supervised-by" className="text-xs text-muted-foreground mb-1 block">Supervisor</label>
                <select
                  id="user-supervised-by"
                  value={form.supervised_by || ''}
                  onChange={(e) => setForm({...form, supervised_by: e.target.value})}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">None</option>
                  {supervisors.map((supervisor) => (
                    <option key={supervisor.id} value={supervisor.id}>{supervisor.name} ({supervisor.email})</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input id="user-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})} className="rounded" />
                <label htmlFor="user-active" className="text-xs text-muted-foreground">Active</label>
              </div>
              <Button type="submit" className="w-full" data-testid="users-save-button">
                {editUser ? 'Update User' : 'Create User'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
